import * as React from 'react'

import { Box } from '@factorialco/deprecated-design-system'
import { F0Dialog, F0Text } from '@factorialco/f0-react'
import {
  CreateDynamicTeamDocument,
  DynamicTeamSpecificationDocument,
  LinkDynamicTeamToGroupDocument,
  UpdateDynamicTeamSpecificationDocument,
} from 'generated/resources/graphql'
import { useDebounceValue, useMediaQuery } from 'usehooks-ts'

import { useMutation } from 'modules/core/hooks/useMutation'
import { useQuery } from 'modules/core/hooks/useQuery'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'

import {
  SpecificationSelector,
  isSpecificationEmpty,
} from 'modules/posts/components/DynamicTeams/SpecificationSelector'

import { EmployeePreviewPanel } from './EmployeePreviewPanel'

interface Props {
  isOpen: boolean
  onClose: () => void
  groupId: number
  groupTitle: string
  dynamicTeamId: number | null | undefined
}

const DEBOUNCE_DELAY_MS = 300

export const SetAutomaticInvitesModal: React.FC<Props> = ({
  isOpen,
  onClose,
  groupId,
  groupTitle,
  dynamicTeamId,
}) => {
  const isWideScreen = useMediaQuery('(min-width: 800px)')

  const [currentSpecification, setCurrentSpecification] = React.useState<string | null>(null)
  const [pendingSpecification, setPendingSpecification] = React.useState<string | null>(null)
  const [isLoading, setIsLoading] = React.useState(false)
  const [error, setError] = React.useState<string | null>(null)
  const [hasChanges, setHasChanges] = React.useState(false)
  const [cannotDisplaySpec, setCannotDisplaySpec] = React.useState(false)

  // Debounce the specification for the preview to avoid excessive API calls
  const specificationForPreview = pendingSpecification ?? currentSpecification
  const [debouncedSpecification] = useDebounceValue(specificationForPreview, DEBOUNCE_DELAY_MS)

  // Only fetch dynamic team specification if we have a dynamicTeamId
  const { data: dynamicTeamData, loading: dynamicTeamLoading } = useQuery(
    DynamicTeamSpecificationDocument,
    {
      variables: {
        ids: dynamicTeamId ? [dynamicTeamId] : [],
        first: 1,
        offsetPage: 1,
      },
      skip: !dynamicTeamId || !isOpen,
      fetchPolicy: 'network-only',
    }
  )

  const [createDynamicTeam] = useMutation(CreateDynamicTeamDocument, {
    extractorFn: (data) => data.postsMutations.createPostsDynamicTeam,
  })
  const [updateDynamicTeam] = useMutation(UpdateDynamicTeamSpecificationDocument, {
    extractorFn: (data) => data.postsMutations.updatePostsDynamicTeam,
  })
  const [linkDynamicTeamToGroup] = useMutation(LinkDynamicTeamToGroupDocument, {
    extractorFn: (data) => data.postsMutations.updatePostsGroup,
  })

  React.useEffect(() => {
    if (!dynamicTeamId) {
      setCurrentSpecification(null)
      setPendingSpecification(null)
      setHasChanges(false)
      return
    }

    const dynamicTeamNodes = dynamicTeamData?.posts?.dynamicTeamsConnection?.nodes
    if (!dynamicTeamNodes?.[0]) {
      return
    }

    const dynamicTeam = dynamicTeamNodes[0]
    const specJson = dynamicTeam.specification ?? null

    setCurrentSpecification(specJson)
    setPendingSpecification(null)
    setHasChanges(false)
  }, [dynamicTeamId, dynamicTeamData])

  const handleSpecificationChange = React.useCallback((newSpecification: string) => {
    setPendingSpecification(newSpecification)
    setHasChanges(true)
    setError(null)
  }, [])

  const handleTransformError = React.useCallback((canDisplay: boolean) => {
    setCannotDisplaySpec(!canDisplay)
  }, [])

  const handleSubmit = async () => {
    setIsLoading(true)
    setError(null)

    const specJson = pendingSpecification ?? currentSpecification

    if (!specJson || isSpecificationEmpty(specJson)) {
      setError(i18n.t('posts.communities.automatic_invites.errors.empty_specification'))
      setIsLoading(false)
      return
    }

    if (dynamicTeamId) {
      const updateResult = await updateDynamicTeam({
        variables: {
          id: dynamicTeamId,
          specification: specJson,
        },
      })

      const handledUpdateResult = handleResult(updateResult, (_errors) => {})
      if (!handledUpdateResult.success) {
        setError(i18n.t('posts.communities.automatic_invites.errors.updating_dynamic_team'))
        setIsLoading(false)
        return
      }
    } else {
      const dynamicTeamName = `${groupTitle} - ${i18n.t(
        'posts.dynamic_team_audience.dynamic_community'
      )}`
      const createResult = await createDynamicTeam({
        variables: {
          name: dynamicTeamName,
          specification: specJson,
        },
      })

      const handledCreateResult = handleResult(createResult, (_errors) => {})
      if (!handledCreateResult.success) {
        setError(i18n.t('posts.communities.automatic_invites.errors.creating_dynamic_team'))
        setIsLoading(false)
        return
      }

      const createData = handledCreateResult.data
      const newDynamicTeamId = createData?.dynamicTeam?.id
      if (!newDynamicTeamId) {
        setError(i18n.t('posts.communities.automatic_invites.errors.creating_dynamic_team'))
        setIsLoading(false)
        return
      }

      // Link the new dynamic team to the group
      const linkResult = await linkDynamicTeamToGroup({
        variables: {
          id: groupId,
          dynamicTeamId: newDynamicTeamId,
        },
      })

      const handledLinkResult = handleResult(linkResult, (_errors) => {})
      if (!handledLinkResult.success || !handledLinkResult?.data?.group?.id) {
        setError(i18n.t('posts.communities.automatic_invites.errors.linking_dynamic_team_to_group'))
        setIsLoading(false)
        return
      }
    }

    // Success - reset state and close modal
    setIsLoading(false)
    setHasChanges(false)
    onClose()
  }

  const handleCancel = () => {
    setError(null)
    onClose()
  }

  const isSubmitting = isLoading
  const isDataLoading = dynamicTeamLoading

  return (
    <F0Dialog
      isOpen={isOpen}
      onClose={onClose}
      width="lg"
      title={i18n.t('posts.communities.automatic_invites.title')}
      description={i18n.t('posts.communities.automatic_invites.description')}
      primaryAction={{
        label: isSubmitting ? i18n.t('common.saving') : i18n.t('common.save'),
        onClick: handleSubmit,
        disabled: isSubmitting || !hasChanges,
        loading: isSubmitting,
      }}
      secondaryAction={{
        label: i18n.t('common.cancel'),
        onClick: handleCancel,
        disabled: isSubmitting,
      }}
      loading={isDataLoading}
    >
      {cannotDisplaySpec && (
        <Box paddingBottom="s16">
          <F0Text
            content={i18n.t(
              'posts.communities.automatic_invites.errors.cannot_display_specification'
            )}
            variant="description"
            color="warning-60"
          />
          <Box paddingTop="s8">
            <F0Text
              content={i18n.t('posts.communities.automatic_invites.errors.create_new_definition')}
              variant="description"
              color="neutral-60"
            />
          </Box>
        </Box>
      )}
      <Box flexDirection={isWideScreen ? 'row' : 'column'} gap="s16" width="full">
        <Box style={isWideScreen ? { flex: '65' } : undefined} width="full">
          <SpecificationSelector
            specification={currentSpecification}
            onChange={handleSpecificationChange}
            onTransformError={handleTransformError}
            disabled={isSubmitting}
            enabledFilters={['location', 'direct_reports_of']}
          />
        </Box>
        <Box
          style={
            isWideScreen
              ? { flex: '35', borderLeft: '1px solid var(--f1-color-border-secondary)' }
              : { borderTop: '1px solid var(--f1-color-border-secondary)' }
          }
          paddingLeft={isWideScreen ? 's16' : undefined}
          paddingTop={isWideScreen ? undefined : 's16'}
          width="full"
        >
          <EmployeePreviewPanel specification={debouncedSpecification} />
        </Box>
      </Box>

      {error && (
        <Box paddingTop="s16">
          <F0Text content={error} variant="description" color="critical-60" />
        </Box>
      )}
    </F0Dialog>
  )
}
